import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, DollarSign, TrendingUp, Briefcase, FileText, Sparkles, Target } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { resolveRole, ROLE_SLUGS } from '../data/roleLibrary';

const CANONICAL_BASE = 'https://ascendforyou.net';
const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

const humanizeSlug = (slug) =>
  slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const Section = ({ children, className = '' }) => (
  <section className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>
);

const RoleLanding = () => {
  const params = useParams();
  const slug = params.role || params.id;
  const role = resolveRole(slug);

  if (!role) {
    return <Navigate to="/free-ats-check" replace />;
  }

  const canonical = `${CANONICAL_BASE}/resume/${slug}`;
  const roleName = humanizeSlug(slug.replace(/-resume$/, ''));

  // FAQ JSON-LD for rich results
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: (role.faqs || []).map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`role-landing-${slug}`}>
      <SEOHead
        title={role.metaTitle}
        description={role.metaDescription}
        canonical={canonical}
        image={`${CANONICAL_BASE}/og-image.png`}
        type="article"
        jsonLd={faqJsonLd}
      />

      {/* Navigation */}
      <nav className="border-b border-[#e5e7eb] bg-white sticky top-0 z-50">
        <Section className="flex items-center justify-between py-4">
          <Link to="/" className="font-satoshi text-xl font-black tracking-tight text-[#0f1115]" data-testid="logo-home">
            ASCEND
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/free-ats-check" className="text-sm text-[#4b5563] hover:text-[#002fa7] transition-colors hidden sm:block" data-testid="nav-free-ats">
              Free ATS Check
            </Link>
            <Link to="/register" className="px-4 py-2 bg-[#002fa7] text-white text-sm font-semibold hover:bg-[#001d6a] transition-colors" data-testid="nav-register">
              Get Started Free
            </Link>
          </div>
        </Section>
      </nav>

      {/* Hero */}
      <Section className="pt-16 pb-12">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#002fa7]/5 text-[#002fa7] text-xs font-bold uppercase tracking-wider mb-5" data-testid="role-badge">
            <Sparkles size={14} /> {roleName} Resume Playbook
          </div>
          <h1 className="font-satoshi text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#0f1115] mb-5 leading-tight" data-testid="role-h1">
            {role.h1}
          </h1>
          <p className="text-lg text-[#4b5563] mb-8 leading-relaxed">{role.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to={`/free-ats-check?role=${encodeURIComponent(roleName)}`}
              className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#002fa7] text-white font-semibold hover:bg-[#001d6a] transition-colors"
              data-testid="hero-cta-ats-check"
            >
              Get My Free ATS Score <ArrowRight size={18} />
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-[#0f1115] text-[#0f1115] font-semibold hover:bg-[#f4f5f7] transition-colors"
              data-testid="hero-cta-signup"
            >
              Optimize with AI (Free)
            </Link>
          </div>
        </motion.div>

        {/* Quick stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-14">
          {[
            { icon: DollarSign, label: 'Salary Range', value: role.salaryRange },
            { icon: TrendingUp, label: 'Job Outlook', value: role.demand },
            { icon: Target, label: 'Top Keywords', value: `${role.keywords?.length || 0} ATS-critical terms` },
          ].map((stat, idx) => (
            <div key={idx} className="p-5 border border-[#e5e7eb] bg-[#fafafa]" data-testid={`stat-${idx}`}>
              <stat.icon size={20} className="text-[#002fa7] mb-2" />
              <div className="text-xs text-[#6b7280] uppercase tracking-wider font-bold mb-1">{stat.label}</div>
              <div className="text-[#0f1115] font-semibold text-sm leading-snug">{stat.value}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Keywords + Skills */}
      <Section className="py-16 border-t border-[#e5e7eb]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-satoshi text-2xl font-black text-[#0f1115] mb-4">ATS Keywords Recruiters Search For</h2>
            <p className="text-[#4b5563] mb-6 text-sm">Hiring systems at top employers are programmed to look for these exact terms. Missing them sinks your application before a human sees it.</p>
            <div className="flex flex-wrap gap-2" data-testid="keyword-list">
              {role.keywords.map(k => (
                <span key={k} className="px-3 py-1.5 bg-[#002fa7]/5 text-[#002fa7] text-sm font-medium">{k}</span>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-satoshi text-2xl font-black text-[#0f1115] mb-4">Core Skills to Feature</h2>
            <p className="text-[#4b5563] mb-6 text-sm">The {roleName} skills that appear most frequently in top job descriptions. Include these prominently in your Skills and Experience sections.</p>
            <ul className="space-y-2" data-testid="skills-list">
              {role.topSkills.map(s => (
                <li key={s} className="flex items-start gap-2">
                  <CheckCircle2 size={18} className="text-[#10b981] flex-shrink-0 mt-0.5" />
                  <span className="text-[#0f1115]">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Sample Bullets */}
      <Section className="py-16 border-t border-[#e5e7eb]">
        <h2 className="font-satoshi text-2xl sm:text-3xl font-black text-[#0f1115] mb-3">Sample {roleName} Resume Bullets</h2>
        <p className="text-[#4b5563] mb-8 max-w-3xl">Model your bullets on these patterns. Each leads with a strong action verb, includes specific metrics, and ties to business outcomes — the exact format recruiters and ATS scoring engines reward.</p>
        <div className="space-y-3" data-testid="sample-bullets">
          {role.sampleBullets.map((b, i) => (
            <div key={i} className="p-5 border-l-4 border-[#002fa7] bg-[#fafafa]">
              <p className="text-[#0f1115] leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 p-6 bg-[#002fa7]/5 border border-[#002fa7]/20">
          <p className="text-[#0f1115] font-semibold mb-2">Want bullets like these — auto-generated for your experience?</p>
          <p className="text-sm text-[#4b5563] mb-4">Ascend's AI rewriter transforms weak job descriptions into recruiter-ready bullets with metrics and impact.</p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#002fa7] text-white font-semibold hover:bg-[#001d6a] transition-colors text-sm"
            data-testid="bullets-cta"
          >
            Try the AI Rewriter (Free) <ArrowRight size={16} />
          </Link>
        </div>
      </Section>

      {/* Interview Prep Teaser */}
      {role.interviewFocus && role.interviewFocus.length > 0 && (
        <Section className="py-16 border-t border-[#e5e7eb]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-satoshi text-2xl sm:text-3xl font-black text-[#0f1115] mb-3">What to Prep for in {roleName} Interviews</h2>
              <p className="text-[#4b5563] mb-6">Once your resume lands interviews, these are the areas top employers consistently test.</p>
              <ul className="space-y-3" data-testid="interview-focus">
                {role.interviewFocus.map((q, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#002fa7] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</div>
                    <span className="text-[#0f1115] leading-relaxed">{q}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-6 bg-[#0f1115] text-white">
              <Briefcase size={24} className="mb-3 opacity-80" />
              <h3 className="font-satoshi text-xl font-bold mb-2">AI Interview Prep Included</h3>
              <p className="text-sm text-white/70 mb-5 leading-relaxed">Generate personalized interview questions, STAR-format sample answers, and company-specific prep — all tuned to your resume and target role.</p>
              <Link to="/register" className="inline-flex items-center gap-2 px-4 py-2 bg-white text-[#0f1115] font-semibold text-sm hover:bg-[#f4f5f7] transition-colors" data-testid="interview-cta">
                Get Interview Prep Free <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </Section>
      )}

      {/* FAQ */}
      <Section className="py-16 border-t border-[#e5e7eb]">
        <h2 className="font-satoshi text-2xl sm:text-3xl font-black text-[#0f1115] mb-8">Frequently Asked Questions</h2>
        <div className="space-y-4 max-w-3xl" data-testid="faq-list">
          {role.faqs.map((f, i) => (
            <details key={i} className="group border border-[#e5e7eb] p-5 hover:border-[#002fa7] transition-colors" data-testid={`faq-${i}`}>
              <summary className="font-satoshi font-bold text-[#0f1115] cursor-pointer list-none flex items-center justify-between gap-4">
                <span>{f.q}</span>
                <span className="text-[#002fa7] group-open:rotate-45 transition-transform text-2xl leading-none flex-shrink-0">+</span>
              </summary>
              <p className="mt-3 text-[#4b5563] leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </Section>

      {/* Related roles */}
      {role.relatedRoles && role.relatedRoles.length > 0 && (
        <Section className="py-12 border-t border-[#e5e7eb]">
          <h2 className="font-satoshi text-lg font-bold text-[#0f1115] mb-4">Related Resume Guides</h2>
          <div className="flex flex-wrap gap-2" data-testid="related-roles">
            {role.relatedRoles.map(r => (
              <Link key={r} to={`/resume/${r}`} className="px-4 py-2 border border-[#e5e7eb] text-sm text-[#0f1115] hover:border-[#002fa7] hover:text-[#002fa7] transition-colors">
                {humanizeSlug(r)} Resume
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* Final CTA */}
      <Section className="py-20 border-t border-[#e5e7eb]">
        <div className="bg-[#002fa7] text-white p-10 text-center">
          <FileText size={32} className="mx-auto mb-4 opacity-80" />
          <h2 className="font-satoshi text-3xl sm:text-4xl font-black mb-3">Ready to optimize your {roleName} resume?</h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">Get your free ATS score in 30 seconds — then use Ascend's AI to rewrite weak sections into interview-winning content.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={`/free-ats-check?role=${encodeURIComponent(roleName)}`} className="px-6 py-3 bg-white text-[#002fa7] font-bold hover:bg-[#f4f5f7] transition-colors" data-testid="final-cta-ats">
              Free ATS Score
            </Link>
            <Link to="/register" className="px-6 py-3 border border-white text-white font-bold hover:bg-white hover:text-[#002fa7] transition-colors" data-testid="final-cta-signup">
              Start Free Account
            </Link>
          </div>
        </div>
      </Section>

      <footer className="py-10 border-t border-[#e5e7eb] text-center text-sm text-[#6b7280]">
        <Section>
          <p>© 2026 Ascend · <Link to="/" className="hover:text-[#002fa7]">Home</Link> · <Link to="/blog" className="hover:text-[#002fa7]">Blog</Link> · <Link to="/pricing" className="hover:text-[#002fa7]">Pricing</Link></p>
          <p className="mt-2 text-xs">More resume guides: {ROLE_SLUGS.slice(0, 8).map((s, i) => (
            <React.Fragment key={s}>
              <Link to={`/resume/${s}`} className="hover:text-[#002fa7]">{humanizeSlug(s)}</Link>
              {i < 7 ? ' · ' : ''}
            </React.Fragment>
          ))}</p>
        </Section>
      </footer>
    </div>
  );
};

export default RoleLanding;
